import React, { useState, useEffect } from "react";
import { compose } from "recompose";
import { Table, Card, Row, Col, Button, Nav, NavItem, NavLink } from "reactstrap";
import {
  TableContainer,
  Pagination,
  TableDataFetch,
  AddNewButton,
  withTable,
  BulkDeleteButton,
  EditDeleteButtons,
  ViewingRecords,
} from "components/common/Tables/index";
import { Page, PendingButton } from "components/common";
import { formatDate, trueToYes } from "services/util";
import { Trans, useTranslation } from "react-i18next";
import { jsonToCsv } from "pages/Xero/Reports/ExportButton";
import { Link } from "react-router-dom";
import DatePicker from 'react-datepicker';
import FormGroup from "pages/Xero/Settings/EmailTemplates/FormGroup";
import Select from "react-select";
import moment from "moment";

const subscriptionTypeLabel = (status) => {
  if (status === "Trial Active" || status === "Trial Inactive") return "Trial";
  return status || "-";
};

const subscriptionStatusBadgeClass = (status) => {
  switch (status) {
    case "Active":
    case "Trial Active":
      return "badge-success";
    case "Suspended":
      return "badge-danger";
    case "Inactive":
    case "Trial Inactive":
      return "badge-warning";
    case "Team":
      return "badge-info";
    default:
      return "badge-secondary";
  }
};

const StatCard = ({ label, value, onClick }) => {
  return (
    <Card body className="px-3 py-2 mb-2">
      <div className="d-flex flex-column align-items-center">
        <div
          style={{ lineHeight: "1.5rem", fontSize: ".875rem" }}
          className="font-weight-semibold text-nowrap"
        >
          <Trans>{label}</Trans>
        </div>
        <div>
          <button
            className="btn btn-link"
            onClick={(e) => {
              e.preventDefault();
              onClick(value);
            }}
          >
            {value.length}
          </button>
        </div>
      </div>
    </Card>
  );
};

export const UserList = ({
  selected,
  match,
  url = "users/organisation_admin_list",
  setSelected,
  ordering,
  setOrdering,
  data,
  setData,
  filters,
  limit,
  offset,
  setOffset,
  setFilters,
  search,
  setSearch,
  isDeleteModalOpen,
  toggleDeleteModal,
  onDeleteItem,
  onDeleted,
  refreshToken,
  readOnly,
  client,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const {t} = useTranslation()
  const [stats, setStats] = useState();

  useEffect(() => {
    (async () => {
      const stats = await client.get("users/user_stats", {
        params: {
          role__in: "Subscriber",
        }
      }).get("data");
      setStats(stats);
    })();
  }, []);

  function onFilter(ids) {
    setFilters({
      ...filters,
      id__in: ids.join(","),
    });
  }

  // const [activeTab, setActiveTab] = useState('organisation');

  // const adminUserList = (userType = 'organisation') => { 
  //   setActiveTab(userType)

  //   let { internal_user, ...userFilters } = filters

  //   if (userType === 'internal') {
  //     userFilters['internal_user'] = true
  //   } 

  //   setFilters({
  //     ...userFilters
  //   })
  // }

  const onExport = async () => {

    setIsExporting(true)

    let exportData = {}

    try {
      exportData = await client.get(`users/organisation_admin_list`, {
        params: {
          ...filters,
          limit: 10000,
          offset: 0
        }
      }).get("data");
      setIsExporting(false)
    } catch (error) {
      setIsExporting(false)
      console.error(error);
    }

    let result = [];
    let headers = [
      t("ID"),
      t("Full Name"),
      t("Company Name"),
      t("Number of Organisations"),
      t("Email"),
      t("Contact Number"),
      t("Tax Number"),
      t("Date Registered"),
      t("Trial End Date"),
      t("User Expired"),
      t("Active"),
    ];
    result.push(headers);

    for (const i of exportData.results) {
      const rowData = [
        i.id,
        i.full_name,
        `"${i.company_name}"`,
        i.number_of_organisations,
        i.email,
        i.contact_number,
        i.tax_number,
        formatDate(i.date_joined),
        formatDate(i.trial_ends_date),
        trueToYes(i.trial_ended),
        trueToYes(i.is_active),
      ];

      result.push(rowData);
    }

    jsonToCsv(result, "SubscriberData");
  };

  const [searchForm, setSearchForm] = useState({})
  const [dateType, setDateType] = useState('registration')
  const [statusOption, setStatusOption] = useState(null)

  const handleInputChange = (event) => {
    setSearchForm({...searchForm, input:event.target.value})
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter") {
      applyFilters();
    }
  };

  const handleDateTypeChange = (opt) => {
    setDateType(opt?.value || 'registration')
  }

  const handleStartDateChange = (date) => {
    setSearchForm({...searchForm, start_date:date})
  };

  const handleEndDateChange = (date) => {
    setSearchForm({...searchForm, end_date:date})
  };

  const handleStatusChange = (opt) => {
    setStatusOption(opt)
  }

  const applyFilters = () => {
    const {
      date_joined__gte,
      date_joined__lte,
      is_active,
      suspended,
      is_internal_user,
      trial_ends_date__gte,
      trial_ends_date__lte,
      subscription_end_date_after,
      subscription_end_date_before,
      search,
      ...next
    } = filters

    if (searchForm.input) {
      next.search = searchForm.input
    }

    if (searchForm.start_date) {
      const d = formatDate(searchForm.start_date)
      if (dateType === 'registration') {
        next.date_joined__gte = moment(searchForm.start_date).startOf('day').format('YYYY-MM-DDTHH:mm:ss')
      } else if (dateType === 'trial') {
        next.trial_ends_date__gte = d
      } else if (dateType === 'subscription') {
        next.subscription_end_date_after = d
      }
    }

    if (searchForm.end_date) {
      const d = formatDate(searchForm.end_date)
      if (dateType === 'registration') {
        next.date_joined__lte = moment(searchForm.end_date).endOf('day').format('YYYY-MM-DDTHH:mm:ss')
      } else if (dateType === 'trial') {
        next.trial_ends_date__lte = d
      } else if (dateType === 'subscription') {
        next.subscription_end_date_before = d
      }
    }

    const today = formatDate(new Date())
    switch (statusOption?.value) {
      case 'active':
        next.is_active = true
        break
      case 'inactive':
        next.is_active = false
        break
      case 'trial_active':
        next.trial_ends_date__gte = today
        break
      case 'trial_inactive':
        next.trial_ends_date__lte = today
        break
      case 'suspended':
        next.suspended = true
        break
      case 'team':
        next.is_internal_user = true
        break
      default:
        break
    }

    setFilters(next)
  }

  const clearFilters = () => {
    setSearchForm({})
    setStatusOption(null)
    setDateType('registration')

    const {
      date_joined__gte,
      date_joined__lte,
      trial_ends_date__gte,
      trial_ends_date__lte,
      subscription_end_date_after,
      subscription_end_date_before,
      is_active,
      suspended,
      is_internal_user,
      search,
      ...next
    } = filters

    setFilters(next)
  }

  return (
    <Page title="Subscribers" className="inv-table" style={{ maxWidth: '1800px', }}>
      <div>
      {stats ? (
        <Row className="mb-2">
          <Col lg>
            <StatCard label="Total" value={stats.total} onClick={onFilter} />
          </Col>
          <Col lg>
            <StatCard
              label="Active trials"
              value={stats.active_trials}
              onClick={onFilter}
            />
          </Col>
          <Col lg>
            <StatCard
              label="Expired trials"
              value={stats.expired_trials}
              onClick={onFilter}
            />
          </Col>
          <Col lg>
            <StatCard
              label="Subscribers with Active subscriptions"
              value={stats.active_subscriptions}
              onClick={onFilter}
            />
          </Col>
          <Col lg>
            <StatCard
              label="Subscribers with Expired subscriptions"
              value={stats.expired_subscribers}
              onClick={onFilter}
            />
          </Col>
          <Col lg>
            <StatCard
              label="Subscribers with no subscription"
              value={stats.non_subscription_users}
              onClick={onFilter}
            />
          </Col>
          <Col lg>
            <StatCard label="Team WP Holding" value={stats.team_wpholding} onClick={onFilter} />
          </Col>
          <Col lg>
            <StatCard
              label="Recent subscribers (last 7 days)"
              value={stats.recent_subscribers}
              onClick={onFilter}
            />
          </Col>
          <Col lg>
            <StatCard
              label="Inactive Subscribers"
              value={stats.inactive_subscribers}
              onClick={onFilter}
            />
          </Col>
        </Row>
      ) : null}
      <div className="mb-2 d-flex align-items-center">
        <AddNewButton
          resource="Subscriber"
          match={match}
          readOnly={readOnly}
        />
        <Button color="primary" className="ml-0" disabled={isExporting} onClick={onExport}>
          {isExporting ? <i className="fa fa-spin fa-spinner mr-2"/> : <i className="fas fa-file-excel mr-2"/>}<Trans>{isExporting ? "Exporting" : "Export"}</Trans>
        </Button>
        <div className="mx-auto" />
        <BulkDeleteButton
          toggleModal={toggleDeleteModal}
          selected={selected}
          url={match.url}
          onDeleted={onDeleted}
          isModalOpen={isDeleteModalOpen}
          readOnly={readOnly}
        />
      </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', padding: '5px' }}>
        <FormGroup label="Search" style={{ flex: 2 }}>
          <input
            className="form-control mb-2"
            placeholder={t("Search by name, company or email")}
            type="text"
            id="searchInput"
            value={searchForm.input || ""}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
          />
        </FormGroup>

        <FormGroup label={t("Date Type")} style={{ flex: 1 }}>
          <Select
            options={[
              { value: 'registration', label: t('Registration Date') },
              { value: 'trial', label: t('Trial Period') },
              { value: 'subscription', label: t('Subscription Expiry') },
            ]}
            style={{ backgroundColor: '#F4F7FE' }}
            value={
              dateType === 'registration'
                ? { value: 'registration', label: t('Registration Date') }
                : dateType === 'trial'
                ? { value: 'trial', label: t('Trial Period') }
                : { value: 'subscription', label: t('Subscription Expiry') }
            }
            onChange={handleDateTypeChange}
            isClearable={false}
          />
        </FormGroup>

        <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', flex: 2 }}>
          <FormGroup label="Start Date" style={{ flex: 1 }}>
            <DatePicker
              className="form-control"
              selected={searchForm.start_date}
              placeholderText={t("Select a Date")}
              onChange={handleStartDateChange}
              dateFormat="YYYY-MM-DD"
              isClearable
            />
          </FormGroup>

          <FormGroup label="End Date" style={{ flex: 1 }}>
            <DatePicker
              className="form-control"
              selected={searchForm.end_date}
              placeholderText={t("Select a Date")}
              onChange={handleEndDateChange}
              dateFormat="YYYY-MM-DD"
              isClearable
            />
          </FormGroup>
        </div>

        <FormGroup label={t("Status")} style={{ flex: 1 }}>
          <Select
            options={[
              { label: t('Active'), value: 'active' },
              { label: t('Inactive'), value: 'inactive' },
              { label: t('Trial Active'), value: 'trial_active' },
              { label: t('Trial Inactive'), value: 'trial_inactive' },
              { label: t('Suspended'), value: 'suspended' },
              { label: t('Team'), value: 'team' },
            ]}
            style={{ backgroundColor: '#F4F7FE' }}
            value={statusOption}
            onChange={handleStatusChange}
            isClearable
            placeholder={t("Select status")}
          />
        </FormGroup>
      </div>
      <div className="d-flex justify-content-end px-2">
        <Button color="primary" onClick={applyFilters}>{t("Apply Filters")}</Button>
        <Button color="secondary" className="ml-2" onClick={clearFilters}>{t("Clear Filters")}</Button>
      </div>
      <TableContainer
        items={data.results}  
        ordering={ordering}
        onOrderingChanged={setOrdering}
        selected={selected}
        onSelectedChanged={setSelected}
      >
        <Table responsive>
          <thead>
            <tr>
              <th select width="5%" />
              <th sortBy="email"><Trans>Email</Trans></th>
              <th sortBy="first_name"><Trans>Name</Trans></th>
              <th sortBy="company_name"><Trans>Company Name</Trans></th>
              <th sortBy="number_of_organisations"><Trans>Number of Organisations</Trans></th>
              <th sortBy="date_joined"><Trans>Registration Date</Trans></th>
              <th><Trans>Subscription Type</Trans></th>
              <th><Trans>Expiry Date</Trans></th>
              <th><Trans>Current Status</Trans></th>
              <th sortBy="email_verified"><Trans>Verified</Trans></th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.results.map((i) => (
              <tr key={i.id}>
                <td select />
                <td>{i.email}</td>
                <td>{i.first_name} {i.last_name}</td>
                <td><Link to={`/core/company-detail/${i.ref_company}`}>{i.company_name}</Link></td>
                <td>{i.number_of_organisations}</td>
                <td>{formatDate(i.date_joined)}</td>
                <td>{t(subscriptionTypeLabel(i.subscription_status))}</td>
                <td>{i.subscription_end_date ? formatDate(i.subscription_end_date) : "-"}</td>
                <td>
                  <span className={`badge ${subscriptionStatusBadgeClass(i.subscription_status)}`}>
                    {t(i.subscription_status)}
                  </span>
                </td>
                <td>
                  {!!i.email_verified ? (
                    <span className="fa fa-check-circle text-success" />
                  ) : (
                    <span className="fa fa-times-circle text-danger" />
                  )}
                </td>
                <td className="text-end text-nowrap">
                  {i.xero_contact_id ? (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`https://go.xero.com/Contacts/View/${i.xero_contact_id}`}
                      className="btn btn-info btn-sm mr-1 text-white"
                    >Xero</a>
                  ) : null}
                  <EditDeleteButtons
                    url={match.url}
                    id={i.id}
                    onDelete={onDeleteItem}
                    readOnly={readOnly}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
      <TableDataFetch
        ordering={ordering}
        refreshToken={refreshToken}
        search={search}
        limit={limit}
        filters={filters}
        url={url}
        offset={offset}
        onDataFetched={setData}
      />  
      <ViewingRecords
        offset={offset}
        limit={limit}
        count={data.count}
        className="float-left"
      />
        <div className="float-right">
          <Pagination
            size="sm"
            count={data.count}
            limit={limit}
            offset={offset}
            onOffsetChanged={setOffset}
          />
        </div>
    </Page>
  );
};

const enhance = compose(withTable({ limit: 100 }));

export default enhance(UserList);
